import { Component, OnInit, inject, HostListener } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { TranslateService } from '@ngx-translate/core';

import { SessionStateService } from './core/session-state.service';
import { AuthService } from './shared/services/auth.service';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet],
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  protected title = 'riskyidIA';
  private translate = inject(TranslateService);
  private router = inject(Router);
  private sessionState = inject(SessionStateService);
  private authService = inject(AuthService);

  @HostListener('window:focus')
  onWindowFocus() {
    if (this.authService.isAuthenticated()) {
      console.log('Window focused: checking for profile updates...');
      this.authService.refreshUserProfile().subscribe();
    }
  }

  @HostListener('window:storage', ['$event'])
  onStorageChange(event: StorageEvent) {
    if (event.newValue === event.oldValue) {
      return;
    }

    if (event.key === 'rol') {
      this.sessionState.hydrate();
      return;
    }

    if (event.key === 'userId') {
      const currentUserId = this.sessionState.userId();
      if (currentUserId === null) {
        return;
      }

      if (event.newValue && event.newValue !== currentUserId) {
        alert(
          this.translate.instant('SessionTerminatedAnotherUserLoggedIn') ||
            'Su sesión ha sido terminada, porque otro usuario inició sesión.',
        );
        this.sessionState.resetMemoryOnly();
        this.redirectToLogin();
        return;
      }

      if (event.newValue === null) {
        alert(
          this.translate.instant('SessionTerminatedLoggedOut') ||
            'La sesión se cerró en otra pestaña/ventana.',
        );
        this.sessionState.resetMemoryOnly();
        this.redirectToLogin();
        return;
      }
    }

    if (event.key === 'token') {
      const currentToken = this.sessionState.token();
      if (currentToken === null) {
        return;
      }

      if (event.newValue === null || event.newValue !== currentToken) {
        this.sessionState.resetMemoryOnly();
        this.redirectToLogin();
      }
    }
  }

  private redirectToLogin() {
    window.location.href = '/login';
  }
  constructor() {
    // Configurar idiomas soportados
    this.translate.addLangs(['es', 'en']);

    // Configurar idioma por defecto
    this.translate.setDefaultLang('en');

    // Intentar obtener el idioma guardado
    const savedLang = localStorage.getItem('language') || 'en';
    this.setLanguage(savedLang);
  }

  private setLanguage(lang: string) {
    this.translate.use(lang).subscribe({
      next: () => {
        console.log('Idioma cambiado a:', lang);
        // Forzar recarga del componente de login para aplicar las traducciones
        const currentUrl = this.router.url;
        if (currentUrl === '/login') {
          this.router
            .navigateByUrl('/', { skipLocationChange: true })
            .then(() => {
              this.router.navigate([currentUrl]);
            });
        }
      },
      error: (err) => {
        console.error('Error al cambiar el idioma:', err);
        this.translate.use('en');
      },
    });
  }

  async ngOnInit() {
    // Mostrar información del entorno en la consola
    console.log('=== Entorno actual ===');
    console.log('Nombre:', environment.name);
    console.log('Producción:', environment.production);
    console.log('Versión:', environment.version);
    console.log('API URL:', environment.apiUrl);
    console.log('Base URL:', environment.baseUrl);
    console.log('Firebase Project ID:', environment.firebase?.projectId);
    console.log('Idioma actual:', this.translate.currentLang);
    console.log('Idiomas disponibles:', this.translate.getLangs());
    console.log('====================');

    // Sincronizar el perfil del usuario con el backend si ya está autenticado
    this.syncProfilePeriodically();

    // Sincronización al navegar entre rutas
    const NavigationEnd = (await import('@angular/router')).NavigationEnd;
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this.authService.isAuthenticated()) {
          this.authService.refreshUserProfile().subscribe();
        }
      }
    });
  }

  private syncProfilePeriodically() {
    // Background heartbeat: refresh role every 5 minutes if authenticated
    setInterval(() => {
      if (this.authService.isAuthenticated()) {
        this.authService.refreshUserProfile().subscribe();
      }
    }, 15 * 1000); // 15 seconds (reduced for better responsiveness)

    // Initial sync
    if (this.authService.isAuthenticated()) {
      this.authService.refreshUserProfile().subscribe({
        next: () =>
          console.log('Sesión sincronizada con el backend al inicio.'),
        error: (err: any) =>
          console.error('Error al sincronizar la sesión al inicio:', err),
      });
    }
  }
}
